/**
 * Management wire types and calls. Field names are the backend's (snake_case), copied from
 * `services/core-api/src/modules/{staff,cart,kitchen,catalog,assistance}`.
 *
 * Only contracts that exist are used. There is no `/management/*` endpoint in the backend (API spec §10 is a
 * design, not an implementation), so locations are read-only and the menu offers one write: availability.
 * Money is never calculated. The menu's prices are shown exactly as sent (integer minor units); every other
 * amount the backend sends a manager here (floor `bill_total`) is dropped at this edge, because no
 * management screen needs it.
 */
import { staffApi } from '@/api/instances';
import type { ApiClient } from '@/api/client';

// ─── Floor: GET /staff/floor ─────────────────────────────────────────────────

export interface FloorSession {
  session_id: string;
  party_display_name: string;
  party_size: number;
  status: string;
  duration_minutes: number;
  has_pending_orders: boolean;
  has_active_assistance: boolean;
}

export interface Location {
  location_id: string;
  location_label: string;
  /** `TABLE` or `SEAT`. */
  location_type: string;
  zone_id: string | null;
  zone_name: string | null;
  nominal_capacity: number;
  /** `VACANT`, `OCCUPIED` or `NEEDS_CLEANING`, as stored. */
  status: string;
  active_sessions: FloorSession[];
}

// ─── Help requests: GET /staff/assistance ────────────────────────────────────

export interface HelpRequest {
  request_id: string;
  type: string;
  status: string;
  created_at: string;
  session_id: string;
  location_label: string;
  location_type: string;
}

// ─── Menu: GET /staff/menu ───────────────────────────────────────────────────

export interface ModifierOption {
  id: string;
  name: string;
  /** Integer minor units, as sent. Shown, never added up. */
  price_delta: number;
}

export interface ModifierGroup {
  id: string;
  name: string;
  min_selection: number;
  max_selection: number;
  options: ModifierOption[];
}

export interface MenuItem {
  id: string;
  name: string;
  description: string | null;
  /** The branch's effective price in integer minor units, as sent. */
  base_price: number;
  is_available: boolean;
  modifier_groups: ModifierGroup[];
}

export interface MenuCategory {
  id: string;
  name: string;
  sort_order: number;
  items: MenuItem[];
}

export interface ManageMenu {
  categories: MenuCategory[];
}

interface WireMenu {
  categories: {
    id: string;
    name: string;
    sort_order: number;
    items: (MenuItem & { tax_rate_basis_points?: number })[];
  }[];
}

/** Keeps what a menu screen shows. The tax rate, which nothing here needs, is left behind. */
export function toMenu(wire: WireMenu): ManageMenu {
  return {
    categories: wire.categories.map((category) => ({
      id: category.id,
      name: category.name,
      sort_order: category.sort_order,
      items: category.items.map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        base_price: item.base_price,
        is_available: item.is_available,
        modifier_groups: item.modifier_groups.map((group) => ({
          id: group.id,
          name: group.name,
          min_selection: group.min_selection,
          max_selection: group.max_selection,
          options: group.options.map((option) => ({
            id: option.id,
            name: option.name,
            price_delta: option.price_delta,
          })),
        })),
      })),
    })),
  };
}

// ─── Availability: POST /kitchen/items/{menu_item_id}/availability ───────────

export interface AvailabilityResult {
  menu_item_id: string;
  is_available: boolean;
  updated_at: string;
}

export function createManageApi(client: ApiClient) {
  return {
    getFloor: async (signal?: AbortSignal): Promise<Location[]> => {
      const wire = await client.get<
        (Omit<Location, 'active_sessions'> & {
          active_sessions: (FloorSession & { bill_total?: number })[];
        })[]
      >('/staff/floor', { signal });
      return wire.map((location) => ({
        ...location,
        active_sessions: location.active_sessions.map((session) => ({
          session_id: session.session_id,
          party_display_name: session.party_display_name,
          party_size: session.party_size,
          status: session.status,
          duration_minutes: session.duration_minutes,
          has_pending_orders: session.has_pending_orders,
          has_active_assistance: session.has_active_assistance,
        })),
      }));
    },

    getHelpRequests: (signal?: AbortSignal) =>
      client.get<HelpRequest[]>('/staff/assistance', { signal }),

    getMenu: async (signal?: AbortSignal): Promise<ManageMenu> =>
      toMenu(await client.get<WireMenu>('/staff/menu', { signal })),

    /**
     * Sets whether a menu item can be ordered at this branch, right now. The backend sets the state it is
     * given (repeating the current state returns it), so no Idempotency-Key is needed or read. The optional
     * `reason` the backend accepts is not stored, so none is sent.
     */
    setAvailability: (menuItemId: string, isAvailable: boolean) =>
      client.post<AvailabilityResult>(
        `/kitchen/items/${encodeURIComponent(menuItemId)}/availability`,
        { is_available: isAvailable },
      ),
  };
}

export const manageApi = createManageApi(staffApi);
